import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { surveyAPI } from '../../utils/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658', '#ff7c7c'];

const SurveyReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);
  const [showIndividualResponses, setShowIndividualResponses] = useState(false);
  const [sortBy, setSortBy] = useState('date_desc');
  const [commentsPage, setCommentsPage] = useState(1);
  const [responsesPage, setResponsesPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    loadAnalytics();
  }, [id]);

  const escapeCsvField = (field) => {
    if (field == null) return '';
    const stringField = String(field);
    if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n') || stringField.includes('\r')) {
      return `"${stringField.replace(/"/g, '""')}"`;
    }
    return stringField;
  };

  const handleDownloadCSV = () => {
    if (!analytics) return;
    
    const csvRows = [];
    
    csvRows.push(['Survey Report']);
    csvRows.push(['Survey Title', escapeCsvField(analytics.survey.title)]);
    csvRows.push(['Manager Name', escapeCsvField(analytics.survey.manager?.full_name || 'N/A')]);
    csvRows.push(['Department', escapeCsvField(analytics.survey.manager?.department || 'N/A')]);
    csvRows.push(['Generated', new Date().toLocaleDateString()]);
    csvRows.push([]);
    
    csvRows.push(['OVERALL STATISTICS']);
    csvRows.push(['Total Responses', analytics.overall.total_responses]);
    csvRows.push(['Average Rating', analytics.overall.average_rating.toFixed(2)]);
    csvRows.push([]);
    
    csvRows.push(['CATEGORY', 'AVERAGE RATING']);
    Object.entries(analytics.by_category || {}).forEach(([category, stats]) => {
      csvRows.push([
        escapeCsvField(category),
        stats.average_rating.toFixed(2)
      ]);
    });
    csvRows.push([]);
    
    if (analytics.comments && analytics.comments.length > 0) {
      csvRows.push(['COMMENTS']);
      csvRows.push(['Category', 'Rating', 'Question', 'Comment']);
      analytics.comments.forEach(comment => {
        csvRows.push([
          escapeCsvField(comment.category),
          comment.rating,
          escapeCsvField(comment.question_text),
          escapeCsvField(comment.comment)
        ]);
      });
    }
    
    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `survey-${analytics.survey.id}-report-${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await surveyAPI.getAnalytics(id);
      setAnalytics(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '2rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    header: {
      borderBottom: '2px solid #dee2e6',
      paddingBottom: '1rem',
      marginBottom: '2rem',
      color: '#000'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem'
    },
    statCard: {
      backgroundColor: '#f8f9fa',
      padding: '1.5rem',
      borderRadius: '8px',
      textAlign: 'center',
      border: '2px solid #dee2e6'
    },
    statValue: {
      fontSize: '2.5rem',
      fontWeight: 'bold',
      color: 'rgb(7, 99, 142)',
      marginBottom: '0.5rem'
    },
    statLabel: {
      color: '#6c757d',
      fontSize: '0.875rem'
    },
    section: {
      marginTop: '2rem',
      padding: '1.5rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px'
    },
    categoryCard: {
      backgroundColor: 'white',
      padding: '1.5rem',
      marginBottom: '1.5rem',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    backBtn: {
      padding: '0.75rem 1.5rem',
      backgroundColor: 'rgb(7, 99, 142)',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      marginBottom: '1rem'
    },
    errorBox: {
      backgroundColor: '#f8d7da',
      color: '#721c24',
      padding: '1.5rem',
      borderRadius: '8px',
      border: '1px solid #f5c6cb'
    },
    commentsSection: {
      marginTop: '2rem',
      padding: '1.5rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px'
    },
    commentCard: {
      backgroundColor: 'white',
      padding: '1rem',
      marginBottom: '1rem',
      borderRadius: '6px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
      borderLeft: '4px solid rgb(7, 99, 142)'
    },
    commentHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.5rem',
      fontSize: '0.875rem',
      color: '#6c757d'
    },
    commentText: {
      color: '#2d3748',
      lineHeight: '1.6',
      marginBottom: '0.5rem'
    },
    commentMeta: {
      fontSize: '0.75rem',
      color: '#a0aec0',
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap'
    },
    ratingStars: {
      color: '#ffc107',
      fontWeight: 'bold'
    },
    downloadBtn: {
      padding: '0.75rem 1.5rem',
      backgroundColor: 'rgb(7, 99, 142)',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      marginBottom: '1rem',
    },
    buttonGroup: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem',
      flexWrap: 'wrap'
    },
    toggleBtn: {
      padding: '0.75rem 1.5rem',
      backgroundColor: 'rgb(7, 99, 142)',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '1rem',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'transform 0.2s'
    },
    individualResponsesSection: {
      marginTop: '2rem',
      padding: '1.5rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px'
    },
    respondentCard: {
      backgroundColor: 'white',
      padding: '1.5rem',
      marginBottom: '1.5rem',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      borderLeft: '4px solid rgb(7, 99, 142)'
    },
    respondentHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
      paddingBottom: '1rem',
      borderBottom: '2px solid #dee2e6'
    },
    respondentMeta: {
      display: 'flex',
      gap: '1.5rem',
      fontSize: '0.875rem',
      color: '#6c757d',
      flexWrap: 'wrap'
    },
    responseTable: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '1rem'
    },
    tableHeader: {
      backgroundColor: '#f8f9fa',
      fontWeight: 'bold',
      textAlign: 'left',
      padding: '0.75rem',
      borderBottom: '2px solid #dee2e6'
    },
    tableCell: {
      padding: '0.75rem',
      borderBottom: '1px solid #dee2e6',
      verticalAlign: 'top'
    },
    sortControls: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    sortBtn: {
      padding: '0.5rem 1rem',
      backgroundColor: 'white',
      border: '2px solid rgb(7, 99, 142)',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.875rem',
      color: 'rgb(7, 99, 142)',
      fontWeight: '600',
      transition: 'all 0.2s'
    },
    sortBtnActive: {
      backgroundColor: 'rgb(7, 99, 142)',
      color: 'white'
    },
    paginationContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '0.5rem',
      marginTop: '1.5rem',
      padding: '1rem'
    },
    pageButton: {
      padding: '0.5rem 1rem',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      backgroundColor: 'white',
      cursor: 'pointer',
      fontSize: '0.875rem'
    },
    pageButtonActive: {
      backgroundColor: 'rgb(7, 99, 142)',
      color: 'white',
      borderColor: 'rgb(7, 99, 142)'
    },
    pageButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    pageInfo: {
      fontSize: '0.875rem',
      color: '#6c757d'
    }
  };

  const getSortedIndividualResponses = () => {
    if (!analytics?.individual_responses) return [];
    
    const responses = [...analytics.individual_responses];
    
    switch(sortBy) {
      case 'date_desc':
        return responses.sort((a, b) => new Date(b.responded_at) - new Date(a.responded_at));
      case 'date_asc':
        return responses.sort((a, b) => new Date(a.responded_at) - new Date(b.responded_at));
      case 'rating_desc':
        return responses.sort((a, b) => b.average_rating - a.average_rating);
      case 'rating_asc':
        return responses.sort((a, b) => a.average_rating - b.average_rating);
      default:
        return responses;
    }
  };

  if (loading) return <div style={styles.container}>Loading analytics...</div>;
  
  if (error) {
    return (
      <div style={styles.container}>
        <button onClick={() => navigate('/admin/surveys')} style={styles.backBtn}>
          Back to Surveys
        </button>
        <div style={styles.errorBox}>
          <h3>Unable to Display Analytics</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!analytics) return <div style={styles.container}>No data available</div>;

  const categoryData = Object.entries(analytics.by_category || {}).map(([category, stats]) => ({
    category,
    average: parseFloat(stats.average_rating),
    responses: stats.response_count
  }));

  const ratingDistribution = Object.entries(analytics.overall?.rating_distribution || {}).map(([rating, count]) => ({
    rating: `${rating} Star${rating !== '1' ? 's' : ''}`,
    count
  }));

  const sortedIndividualResponses = getSortedIndividualResponses();

  return (
    <div style={styles.container}>
      <div style={styles.buttonGroup}>
        <button onClick={() => navigate('/admin/surveys')} style={styles.backBtn}>
          Back to Surveys
        </button>
        <button onClick={() => navigate(`/admin/surveys/${id}`)} style={styles.backBtn}>
          Back to Survey Details
        </button>
        <button onClick={handleDownloadCSV} style={styles.downloadBtn}>
          Download CSV Report
        </button>
      </div>

      <div style={styles.header}>
        <h2>Survey Analytics Report</h2>
        <h3>{analytics.survey?.title}</h3>
        <p style={{color: '#6c757d'}}>{analytics.survey?.description}</p>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{analytics.overall?.total_responses || 0}</div>
          <div style={styles.statLabel}>Total Responses</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{parseFloat(analytics.overall?.average_rating || 0).toFixed(2)}</div>
          <div style={styles.statLabel}>Overall Average Rating</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{Object.keys(analytics.by_category || {}).length}</div>
          <div style={styles.statLabel}>Categories Evaluated</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{analytics.survey?.questions_count || 0}</div>
          <div style={styles.statLabel}>Total Questions</div>
        </div>
      </div>

      {ratingDistribution.length > 0 && (
        <div style={styles.section}>
          <h3>Rating Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ratingDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="rating" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="rgb(7, 99, 142)" name="Number of Responses" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={styles.section}>
        <h3>Category Performance</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={categoryData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="category" />
            <YAxis domain={[0, 5]} />
            <Tooltip />
            <Legend />
            <Bar dataKey="average" fill="rgb(7, 99, 142)" name="Average Rating (out of 5)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={styles.section}>
        <h3>Detailed Category Breakdown</h3>
        {Object.entries(analytics.by_category || {}).map(([category, stats]) => (
          <div key={category} style={styles.categoryCard}>
            <h4>{category}</h4>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '1rem'}}>
              <div>
                <strong>Average Rating:</strong>
                <div style={{fontSize: '1.5rem', color: 'rgb(7, 99, 142)'}}>
                  {parseFloat(stats.average_rating).toFixed(1)} / 5.0
                </div>
              </div>
              <div>
                <strong>Responses:</strong>
                <div style={{fontSize: '1.5rem', color: 'rgb(7, 99, 142)'}}>
                  {stats.response_count}
                </div>
              </div>
              <div>
                <strong>Questions:</strong>
                <div style={{fontSize: '1.5rem', color: '#6c757d'}}>
                  {stats.question_count}
                </div>
              </div>
            </div>
            
            {stats.questions && stats.questions.length > 0 && (
              <div style={{marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #dee2e6'}}>
                <strong>Questions in this category:</strong>
                <ul style={{marginTop: '0.5rem'}}>
                  {stats.questions.map((q, idx) => (
                    <li key={idx} style={{marginBottom: '0.5rem'}}>
                      {q.text} - <span style={{color: 'rgb(7, 99, 142)', fontWeight: 'bold'}}>
                        Avg: {parseFloat(q.average_rating).toFixed(2)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {analytics.comments && analytics.comments.length > 0 && (
        <div style={styles.commentsSection}>
          <h3>Survey Comments ({analytics.comments.length})</h3>
          <p style={{color: '#6c757d', marginBottom: '1.5rem'}}>
            Feedback and comments from respondents, sorted by most recent
          </p>
          {analytics.comments.slice((commentsPage - 1) * itemsPerPage, commentsPage * itemsPerPage).map((comment) => (
            <div key={comment.id} style={styles.commentCard}>
              <div style={styles.commentHeader}>
                <strong>{comment.category}</strong>
                <span style={styles.ratingStars}>
                  {'★'.repeat(comment.rating)}{'☆'.repeat(5 - comment.rating)}
                </span>
              </div>
              <div style={styles.commentText}>
                "{comment.comment}"
              </div>
              <div style={styles.commentMeta}>
                <span> {comment.question_text}</span>
                <span> {new Date(comment.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
          {analytics.comments.length > itemsPerPage && (
            <div style={styles.paginationContainer}>
              <button 
                onClick={() => setCommentsPage(p => Math.max(1, p - 1))}
                disabled={commentsPage === 1}
                style={{
                  ...styles.pageButton,
                  ...(commentsPage === 1 ? styles.pageButtonDisabled : {})
                }}
              >
                Previous
              </button>
              {Array.from({ length: Math.ceil(analytics.comments.length / itemsPerPage) }, (_, i) => i + 1).map(pageNum => (
                <button
                  key={pageNum}
                  onClick={() => setCommentsPage(pageNum)}
                  style={{
                    ...styles.pageButton,
                    ...(commentsPage === pageNum ? styles.pageButtonActive : {})
                  }}
                >
                  {pageNum}
                </button>
              ))}
              <button 
                onClick={() => setCommentsPage(p => Math.min(Math.ceil(analytics.comments.length / itemsPerPage), p + 1))}
                disabled={commentsPage >= Math.ceil(analytics.comments.length / itemsPerPage)}
                style={{
                  ...styles.pageButton,
                  ...(commentsPage >= Math.ceil(analytics.comments.length / itemsPerPage) ? styles.pageButtonDisabled : {})
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {analytics.individual_responses && analytics.individual_responses.length > 0 && (
        <div style={{marginTop: '2rem'}}>
          <button 
            onClick={() => setShowIndividualResponses(!showIndividualResponses)}
            style={styles.toggleBtn}
          >
            {showIndividualResponses ? 'Hide' : 'Show'} Individual Responses ({analytics.individual_responses.length})
          </button>

          {showIndividualResponses && (
            <div style={styles.individualResponsesSection}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                <h3 style={{margin: 0}}>Individual Survey Responses</h3>
                <div style={styles.sortControls}>
                  <span style={{fontSize: '0.875rem', color: '#6c757d', fontWeight: '600'}}>Sort by:</span>
                  <button 
                    onClick={() => setSortBy('date_desc')}
                    style={{...styles.sortBtn, ...(sortBy === 'date_desc' ? styles.sortBtnActive : {})}}
                  >
                    Date ↓
                  </button>
                  <button 
                    onClick={() => setSortBy('date_asc')}
                    style={{...styles.sortBtn, ...(sortBy === 'date_asc' ? styles.sortBtnActive : {})}}
                  >
                    Date ↑
                  </button>
                  <button 
                    onClick={() => setSortBy('rating_desc')}
                    style={{...styles.sortBtn, ...(sortBy === 'rating_desc' ? styles.sortBtnActive : {})}}
                  >
                    Rating ↓
                  </button>
                  <button 
                    onClick={() => setSortBy('rating_asc')}
                    style={{...styles.sortBtn, ...(sortBy === 'rating_asc' ? styles.sortBtnActive : {})}}
                  >
                    Rating ↑
                  </button>
                </div>
              </div>

              {sortedIndividualResponses.slice((responsesPage - 1) * itemsPerPage, responsesPage * itemsPerPage).map((respondent) => (
                <div key={respondent.respondent_id} style={styles.respondentCard}>
                  <div style={styles.respondentHeader}>
                    <div>
                      <h4 style={{margin: 0, color: 'rgb(7, 99, 142)', fontSize: '1.25rem'}}>
                        {respondent.respondent_label}
                      </h4>
                      <div style={styles.respondentMeta}>
                        <span>{new Date(respondent.responded_at).toLocaleDateString()}</span>
                        <span>{new Date(respondent.responded_at).toLocaleTimeString()}</span>
                        {/* <span>{respondent.email_masked}</span> */}
                      </div>
                    </div>
                    <div style={{textAlign: 'right'}}>
                      <div style={{fontSize: '2rem', fontWeight: 'bold', color: 'rgb(7, 99, 142)'}}>
                        {respondent.average_rating.toFixed(2)}
                      </div>
                      <div style={{fontSize: '0.875rem', color: '#6c757d'}}>
                        Average Rating
                      </div>
                      <div style={{fontSize: '0.75rem', color: '#a0aec0', marginTop: '0.25rem'}}>
                        {respondent.total_responses} responses
                      </div>
                    </div>
                  </div>

                  <table style={styles.responseTable}>
                    <thead>
                      <tr>
                        <th style={{...styles.tableHeader, width: '20%'}}>Category</th>
                        <th style={{...styles.tableHeader, width: '15%'}}>Rating</th>
                        <th style={{...styles.tableHeader, width: '65%'}}>Comment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {respondent.responses.map((response) => (
                        <tr key={response.question_id}>
                          <td style={styles.tableCell}>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#e6f2f7',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              color: 'rgb(7, 99, 142)'
                            }}>
                              {response.category}
                            </span>
                          </td>
                          <td style={styles.tableCell}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                              <span style={{fontSize: '1.25rem', fontWeight: 'bold', color: 'rgb(7, 99, 142)'}}>
                                {response.rating}
                              </span>
                              <span style={{fontSize: '0.875rem', color: '#6c757d'}}>/5</span>
                            </div>
                            <div style={{color: '#ffc107', fontSize: '0.875rem'}}>
                              {'★'.repeat(response.rating)}{'☆'.repeat(5 - response.rating)}
                            </div>
                          </td>
                          <td style={styles.tableCell}>
                            {response.comment ? (
                              <div style={{
                                fontStyle: 'italic',
                                color: '#495057',
                                backgroundColor: '#f8f9fa',
                                padding: '0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                              }}>
                                "{response.comment}"
                              </div>
                            ) : (
                              <span style={{color: '#a0aec0', fontSize: '0.875rem'}}>No comment</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {sortedIndividualResponses.length > itemsPerPage && (
                <div style={styles.paginationContainer}>
                  <button 
                    onClick={() => setResponsesPage(p => Math.max(1, p - 1))}
                    disabled={responsesPage === 1}
                    style={{
                      ...styles.pageButton,
                      ...(responsesPage === 1 ? styles.pageButtonDisabled : {})
                    }}
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.ceil(sortedIndividualResponses.length / itemsPerPage) }, (_, i) => i + 1).map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => setResponsesPage(pageNum)}
                      style={{
                        ...styles.pageButton,
                        ...(responsesPage === pageNum ? styles.pageButtonActive : {})
                      }}
                    >
                      {pageNum}
                    </button>
                  ))}
                  <button 
                    onClick={() => setResponsesPage(p => Math.min(Math.ceil(sortedIndividualResponses.length / itemsPerPage), p + 1))}
                    disabled={responsesPage >= Math.ceil(sortedIndividualResponses.length / itemsPerPage)}
                    style={{
                      ...styles.pageButton,
                      ...(responsesPage >= Math.ceil(sortedIndividualResponses.length / itemsPerPage) ? styles.pageButtonDisabled : {})
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SurveyReport;
